import type { NotificationConditionType } from '@cims-online/shared'
import { Divider, Form, Typography } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import RecipientSelect from 'components/CIMSSelect/common/customWorkflows/RecipientSelect'
import { summarizeNotificationConfiguration } from '../../../utils/summary'
import type {
  NotificationField,
  NotificationStatus,
  NotificationTemplate,
} from '../../../types'
import type { NotificationFormValues } from '../types'
import SettingsCard from './SettingsCard'
import { toSubmission as toConditionSubmission } from './Conditions/utils/normalization'
import { toSubmission as toTriggerSubmission } from './TriggerSettings/utils/normalization'
import { labeledValueString } from '../utils/referenceValues'

type SendSettingsProps = {
  fields: NotificationField[]
  statuses: NotificationStatus[]
  templates: NotificationTemplate[]
}

function validConditions(
  conditions: NotificationFormValues['conditions'] | undefined
) {
  return (conditions ?? []).flatMap<NotificationConditionType>(
    (condition) => toConditionSubmission([condition]) ?? []
  )
}

/**
 * Renders notification delivery controls and a live configuration summary.
 *
 * Templates are limited to active records for the current workflow and then
 * filtered to the selected trigger before they can be chosen.
 */
function SendSettings({ fields, statuses, templates }: SendSettingsProps) {
  const trigger = Form.useWatch('trigger')
  const compatibleTemplates = templates.filter(
    (template) => template.trigger === trigger
  )
  const templateOptions = useOptions({
    items: compatibleTemplates,
    label: 'name',
    sort: 'name',
    value: 'id',
  })

  return (
    <SettingsCard style={{ marginTop: 16 }}>
      <Typography.Title
        level={3}
        style={{ fontSize: 16, lineHeight: 1.5, margin: '0 0 24px' }}
      >
        Send Email
      </Typography.Title>
      <CIMSFormItem
        label="Template"
        name="templateId"
        requiredMessage="Please select a notification template"
      >
        <CIMSSelect
          disabled={!trigger}
          options={templateOptions}
          placeholder={trigger ? 'Select a template' : 'Select a trigger first'}
        />
      </CIMSFormItem>
      <CIMSFormItem label="To" name="to">
        <RecipientSelect placeholder="Select recipients or enter an email" />
      </CIMSFormItem>
      <Divider style={{ borderTopColor: 'rgb(184, 193, 204)' }} />
      <Form.Item noStyle shouldUpdate>
        {({ getFieldsValue }) => {
          const values = getFieldsValue(true) as NotificationFormValues
          const triggerSubmission = toTriggerSubmission(values)
          const templateId = labeledValueString(values.templateId)
          const templateName =
            templates.find(({ id }) => id === templateId)?.name ??
            (typeof values.templateId?.label === 'string'
              ? values.templateId.label
              : undefined)
          const selectedTemplate = templateName
            ? {
                deleted: !!values.templateId?.deleted,
                name: templateName,
              }
            : undefined

          return (
            <Typography.Paragraph
              aria-live="polite"
              style={{ color: '#595959', marginBottom: 0 }}
            >
              {summarizeNotificationConfiguration(
                {
                  conditions: validConditions(values.conditions),
                  recipientCount: values.to?.length ?? 0,
                  template: selectedTemplate,
                  trigger: triggerSubmission?.trigger ?? values.trigger,
                  triggerSettings: triggerSubmission?.triggerSettings ?? null,
                },
                { fields, statuses }
              )}
            </Typography.Paragraph>
          )
        }}
      </Form.Item>
    </SettingsCard>
  )
}

export default SendSettings
